import { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Information } from '@carbon/icons-react';
import { Tooltip } from '@carbon/react';

import styles from './CategoryTags.module.scss';

import TagsInput from 'Components/Common/TagsInput/TagsInput';
import { InputElement } from 'Components/Common/TagsInput/types';
import { useCreateCategoryMutation, useDeleteCategoryMutation, useGetCategoriesByEntityQuery } from 'api/categoryEndpoints';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import useGetUserPermission from 'hooks/useGetUserPermission';
import CategoryEditModal from './CategoryEditModal/CategoryEditModal';
import { CategoryDto } from 'api/dtos/category';
import useGetCurrentEntity from 'hooks/useGetCurrentEntity';
import Tag from 'Components/Common/Tag/Tag';
import useGetCurrentSubscription from 'Screens/Dashboard/Subscription/useGetCurrentSubscription';

const CategoryTags = () => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const currentEntity = useGetCurrentEntity();
    const {
        data, isFetching, isError, refetch,
    } = useGetCategoriesByEntityQuery(currentEntityId ?? '', {
        skip: !currentEntityId,
    });
    const [onAddCategory, { isLoading: addLoading }] = useCreateCategoryMutation();
    const { isAdmin, isModerator, isOwner } = useGetUserPermission();
    const {
        activeSubscription,
        isFetching: isFetchingSubscription,
        isPaidPlan,
    } = useGetCurrentSubscription();
    const hasPermission = isAdmin || isModerator || isOwner;
    const hasSubscriptionAccess = isPaidPlan && activeSubscription?.hasAccess !== false;
    const isAccessDisabled = !hasPermission || (!isFetchingSubscription && !hasSubscriptionAccess);

    const [onRemoveCategory, { isLoading: removeLoading }] = useDeleteCategoryMutation();

    const categories = useMemo(() => data ?? [], [data]);
    const canManageCategories = 
        Boolean((currentEntity?.isParish &&
        !currentEntity.parentId) ||
        currentEntity?.isIndependent);

    const [categoryToEdit, setCategoryToEdit] = useState<CategoryDto | null>(null);

    const onAdd = useCallback(async (name: string) => {
        if (!currentEntityId) {
            return;
        }
        await onAddCategory({
            entityId: currentEntityId,
            data: {
                name,
            },
        }).unwrap();
    }, [currentEntityId, onAddCategory]);

    const onRemove = useCallback(async (category: InputElement | null) => {
        if (!category || !category.id) {
            return;
        }

        if (!currentEntityId) {
            return;
        }
        try {
            await onRemoveCategory({ entityId: currentEntityId, id: category.id });
        } catch (e) {
            // handled in rtk query
        }
    }, [currentEntityId, onRemoveCategory]);

    const onClick = useCallback((id?: string) => {
        if (!canManageCategories) {
            return;
        }

        const category = categories.find((cat) => cat.id === id) || null;
        setCategoryToEdit(category);
    }, [canManageCategories, categories]);

    const onCategoryModalClose = useCallback(() => {
        setCategoryToEdit(null);
    }, []);

    if (isError) {
        return (
            <div className={styles.errorContainer}>
                <ErrorActionState className={styles.error} onClick={refetch} />
            </div>
        );
    }

    if (canManageCategories) {
        return (
            <>
                <TagsInput
                    textInputId="category-tags-input"
                    elements={categories}
                    onAdd={onAdd}
                    isDisabled={isAccessDisabled || isFetchingSubscription || isFetching || addLoading || removeLoading}
                    disabledTooltip={isAccessDisabled ? intl.formatMessage(messages.disabledTooltip) : undefined}
                    onRemove={onRemove}
                    onClick={onClick}
                    messages={messages}
                />
                <CategoryEditModal open={!!categoryToEdit} onClose={onCategoryModalClose} category={categoryToEdit} />
            </>
        );
    }

    return (
        <section className={styles.readonlyContainer}>
            <div className={styles.readonlyHeader}>
                <span className={styles.readonlyTitle}>{intl.formatMessage(messages.title)}</span>
                <Tooltip
                    label={intl.formatMessage(messages.childReadonlyTooltip)}
                    align="bottom"
                >
                    <Information size={16} />
                </Tooltip>
            </div>
            <div className={styles.readonlyTags}>
                {categories.length > 0 ? categories.map((category) => (
                    <Tag key={category.id} color={category.color}>
                        {category.name}
                    </Tag>
                )) : (
                    <p className={styles.emptyText}>{intl.formatMessage(messages.noCategories)}</p>
                )}
            </div>
        </section>
    );
};

const messages = defineMessages({
    title: {
        id: 'src.Screens.Dashboard.EventsConfiguration.Inputs.CategoryTags.CategoryTags.title',
        defaultMessage: 'Kategorie dla wydarzeń',
    },
    titlePlaceholder: {
        id: 'src.Screens.Dashboard.EventsConfiguration.Inputs.CategoryTags.CategoryTags.titlePlaceholder',
        defaultMessage: 'Wprowadź nazwę nowej kategorii',
    },
    elementAlreadyExist: {
        id: 'src.Screens.Dashboard.EventsConfiguration.Inputs.CategoryTags.CategoryTags.elementAlreadyExist',
        defaultMessage: 'Taka kategoria już istnieje. Dodaj inną.',
    },
    formName: {
        id: 'src.Screens.Dashboard.EventsConfiguration.Inputs.CategoryTags.CategoryTags.formName',
        defaultMessage: 'Formularz kategorii',
    },
    childReadonlyTooltip: {
        id: 'src.Screens.Dashboard.EventsConfiguration.Inputs.CategoryTags.CategoryTags.childReadonlyTooltip',
        defaultMessage: 'Kategorie wydarzen w podgrupie sa synchronizowane z parafii. Dodawanie, edycja i usuwanie kategorii jest dostepne tylko na poziomie parafii.',
    },
    noCategories: {
        id: 'src.Screens.Dashboard.EventsConfiguration.Inputs.CategoryTags.CategoryTags.noCategories',
        defaultMessage: 'Brak kategorii skonfigurowanych w parafii.',
    },
    disabledTooltip: {
        id: 'src.Screens.Dashboard.EventsConfiguration.Inputs.CategoryTags.CategoryTags.disabledTooltip',
        defaultMessage: 'Nie masz odpowiednich uprawnień w tej grupie albo nie masz wykupionej odpowiedniej subskrypcji koniecznej do zarządzania kategoriami wydarzeń.',
    },
});

export default CategoryTags;
